import { AxiosResponse, isAxiosError } from 'axios';

const FILENAME_STAR_REGEX = /filename\*=([^;]+)/i;
/** quoted names may contain a `;`, unquoted ones end at the next parameter */
const QUOTED_FILENAME_REGEX = /filename="([^"]*)"/i;
const FILENAME_REGEX = /filename=([^;]+)/i;
/** rfc 5987 prefixes the value with `<charset>'<language>'` */
const CHARSET_PREFIX_REGEX = /^[^']*'[^']*'/;

export function getContentDispositionFilename(contentDisposition: string | undefined | null): string | null {
  if (contentDisposition == null) return null;

  const starMatch = FILENAME_STAR_REGEX.exec(contentDisposition);
  if (starMatch?.[1] != null) {
    const encoded = starMatch[1].trim().replace(CHARSET_PREFIX_REGEX, '');
    try {
      return decodeURIComponent(encoded);
    } catch {
      return encoded;
    }
  }

  const quotedMatch = QUOTED_FILENAME_REGEX.exec(contentDisposition);
  if (quotedMatch?.[1] != null) return quotedMatch[1].trim();

  const match = FILENAME_REGEX.exec(contentDisposition);
  return match?.[1] != null ? match[1].trim() : null;
}

/** Triggers a browser download for an already fetched blob, then releases the object url. */
export function saveBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Triggers a browser download for a blob response, honoring the Content-Disposition filename
 * when the server sends one.
 */
export function saveBlobResponse(response: AxiosResponse<Blob>, fallbackFilename: string) {
  const filename = getContentDispositionFilename(response.headers?.['content-disposition']) ?? fallbackFilename;

  saveBlob(new Blob([response.data]), filename);
}

// FileReader rather than Blob.text(): jsdom implements only the former.
function readBlobText(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/**
 * A blob request receives its JSON error body as a Blob, whose `type` is a MIME type the notification would
 * show as an error code; decoding it lets the real code through.
 */
export async function rethrowDecodedBlobError(error: unknown): Promise<never> {
  if (isAxiosError(error) && error.response?.data instanceof Blob) {
    try {
      error.response.data = JSON.parse(await readBlobText(error.response.data));
    } catch {
      // Dropping an undecodable body keeps the axios error itself, so the status survives for the caller.
      error.response.data = undefined;
    }
  }
  throw error;
}
